import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';

import { reportChartConfigs } from './reportChartConfig';
import { ChartRenderer } from '@/componentsV1/Charts';
import { CardTileHeading } from '../CardTileHeading';
import { EmptyReportChart } from './EmptyReportChart';

interface ReportChartsProps {
  chartKey: string;
  chartData: any;
  width?: string | number;
  height?: string | number;
  isFullScreen?: boolean;
  onDrillDownOpen?: (_data: any) => void;
  isFlow?: boolean;
  isStaticReport?: boolean;
  isPrintView?: boolean;
  tileData?: any;
  isEditMode?: boolean;
  uniqueTileId?: string;
  onTileMetaChange?: (_tileId: string, _metadata: any) => void;
  /** Display labels for applied filters not supported by this block */
  unsupportedFilterLabels?: string[];
}

/**
 * ReportCharts
 *
 * This component uses ChartRenderer to create a chart for reports
 * chart experience. It uses the useChartLogic hook to manage chart state and operations.
 *
 * @param chartKey - The unique identifier for a chart
 * @param chartData - The initial data for the chart
 * @param width - The width of the chart container
 * @param height - The height of the chart container
 */
const ReportCharts: React.FC<ReportChartsProps> = ({
  chartKey,
  chartData,
  tileData,
  isFullScreen,
  onDrillDownOpen,
  isFlow = false,
  isStaticReport = false,
  isPrintView = false,
  onTileMetaChange,
  uniqueTileId,
  isEditMode,
  unsupportedFilterLabels,
}) => {
  const [dynamicHeight, setDynamicHeight] = useState<string>('300px');
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    let timeoutId;

    const calculateHeight = () => {
      if (containerRef.current) {
        const height = containerRef.current.clientHeight;

        if (height > 0) {
          // Use 95% of container height, minimum 300px
          const heightString = `${Math.max(Math.round(height * 0.95), 300)}px`;

          setDynamicHeight((prev) => {
            // Only update if significantly different (>5px) to allow expansion
            const prevNum = parseInt(prev.replace('px', ''));
            const newNum = parseInt(heightString.replace('px', ''));
            return Math.abs(prevNum - newNum) > 5 ? heightString : prev;
          });
        }
      }
    };

    // Debounced version to prevent infinite loops
    const debouncedCalculateHeight = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(calculateHeight, 100);
    };

    // Initial calculation
    calculateHeight();

    // Add resize listener
    window.addEventListener('resize', debouncedCalculateHeight);

    // ResizeObserver with debouncing
    const resizeObserver = new ResizeObserver(debouncedCalculateHeight);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', debouncedCalculateHeight);
      resizeObserver.disconnect();
    };
  }, []);

  const {
    title,
    defaultChart: defaultFromConstant,
    metricDefinition,
    basedOn,
    category,
    singleSeriesData,
    xTitle,
    yTitle,
    legendName,
    seriesName,
    skipDt,
    isPipeline,
    chartContainerStyle,
    chartStyle,
    hideAxisValues,
    legendOrderMatchesChart,
  } = reportChartConfigs[chartKey] || {};

  const defaultChart = tileData?.metadata?.defaultChart || defaultFromConstant;

  const chartType = useSelector((state: any) => {
    //  chart keys for static vs custom reports
    const chartKeyForState = isStaticReport
      ? `static_${chartKey}`
      : (uniqueTileId ?? chartKey);
    return (
      (isStaticReport
        ? state.staticReports.chartTypes[chartKeyForState]
        : state.customReports.chartTypes[chartKeyForState]) ||
      defaultChart ||
      'line'
    );
  });

  // Initialize chart type on first mount if not already set
  useEffect(() => {
    // Only initialize if we're in edit mode or if there's no existing chart type in Redux
    // This prevents overriding user-selected chart types when expanding
    if (isEditMode) {
      const chartKeyForState = isStaticReport
        ? `static_${chartKey}`
        : (uniqueTileId ?? chartKey);

      dispatch({
        type: isStaticReport
          ? 'staticReports/setChartType'
          : 'customReports/setChartType',
        payload: { chartKey: chartKeyForState, chartType: defaultChart },
      });
    }
  }, [
    chartKey,
    isStaticReport,
    defaultChart,
    dispatch,
    tileData,
    isEditMode,
    uniqueTileId,
  ]);

  const isChartEmpty = useCallback((chartData) => {
    const series = chartData?.series;
    if (!Array.isArray(series) || series.length === 0) {
      return true; // Empty or non-existent series
    }

    // Check if all series items have `y` values equal to 0
    if (Object.prototype.hasOwnProperty.call(series[0], 'y')) {
      return series.every((item) => item?.y === 0);
    }

    // Check if all values in series are directly equal to 0
    if (series.every((i) => i === 0)) {
      return true;
    }

    // Check if all `data` items within series have `y` values equal to 0
    if (Object.prototype.hasOwnProperty.call(series[0], 'data')) {
      return series.every((item) => item?.data?.every((i) => i.y === 0));
    }

    return false; // If none of the above conditions match, series is not empty
  }, []);

  const isEmpty = isChartEmpty(chartData);
  const drilldownTitle = title;

  return (
    <Flex h="100%" w="100%" flexDir="column">
      <CardTileHeading
        tileId={tileData?.id}
        label={tileData?.metadata?.title ?? title}
        metricDefinition={isFullScreen ? undefined : metricDefinition}
        basedOn={basedOn}
        category={category}
        isStaticReport={isStaticReport}
        onTileMetaChange={onTileMetaChange}
        subjectType={tileData?.subject_type}
        unsupportedFilterLabels={unsupportedFilterLabels}
				isPrintView={isPrintView}
      />
      {isEmpty ? (
        <EmptyReportChart />
      ) : (
        <Box
          mt="15px"
          h="100%"
          w="100%"
          maxW="100%"
          overflow="hidden"
          ref={containerRef}
          {...(isStaticReport ? chartContainerStyle : {})}
        >
          <ChartRenderer
            key={chartType}
            data={chartData}
            chartType={chartType}
            height={dynamicHeight}
            customOptions={{
              singleSeriesData,
              legendName,
            }}
            yAxis={drilldownTitle}
            highchartsOptions={{
              ...(legendOrderMatchesChart && { legend: { reversed: false } }),
              yAxis: {
                title: {
                  text: hideAxisValues ? null : yTitle || null,
                },
                ...(hideAxisValues && {
                  labels: { enabled: false },
                  lineWidth: 0,
                  gridLineWidth: 0,
                }),
                ...(legendOrderMatchesChart && { reversedStacks: false }),
              },
              xAxis: {
                title: {
                  text: hideAxisValues ? null : xTitle || null,
                },
                categories: chartData?.categories,
                ...(hideAxisValues && {
                  labels: { enabled: false },
                  lineWidth: 0,
                  gridLineWidth: 0,
                }),
              },
            }}
            isPipeline={isPipeline}
            onDrillDownOpen={onDrillDownOpen}
            isFlow={isFlow}
            seriesName={seriesName}
            skipDt={skipDt}
            chartStyle={chartStyle}
            isStaticReport={isStaticReport}
            isPrintView={isPrintView}
          />
        </Box>
      )}
    </Flex>
  );
};

export default ReportCharts;
